import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCenter } from "../../../center";
import { PatientOwnershipBadge, useCouplePatient, RecommendationsCard, CouplePatientChatTab } from "../../../patient";
import type { CouplePatient } from "../../../patient";
import { useParams, useNavigate, useSearchParams } from "react-router";
import { motion, AnimatePresence } from "framer-motion";
import PageMeta from "../../../../shared/ui/common/PageMeta";
import GroupedTabNav from "../../../../shared/ui/tabs/GroupedTabNav";
import { PageHeader } from "../../../../shared/ui/primitives";
import { useMotionSafe } from "../../../../hooks/useMotionSafe";

const PANEL_ID = "couple-patient-panel";
import ComponentCard from "../../../../shared/ui/common/ComponentCard";
import Button from "../../../../shared/ui/button/Button";
import { getAvatarColor } from "../../../../shared/utils/avatar";
import { Users, Calendar, Mail, Phone, Trash2, LayoutDashboard, History as HistoryIcon, FileText, Lightbulb, AlertTriangle, Loader2, BarChart2, ListChecks, Sparkles, ChevronRight, MessageSquare, Bell, Highlighter } from "lucide-react";

import { useSubscription } from "../../../subscription";
import { useAiInsightsGate } from "../../../ai_insights";
import SubscriptionRestrictedState from "../../../../shared/ui/common/SubscriptionRestrictedState";
import PatientViewSessions from "../components/PatientViewSessions";
import PatientViewTherapeuticPlans from "../components/PatientViewTherapeuticPlans";
import PatientAlertsTab from "../components/PatientAlertsTab";
import PatientAgreementsTab from "../components/PatientAgreementsTab";
import { PatientHighlightsTab } from "../../../session";
import { listCoupleSessions } from "../../application/sessions";
import type { TriggeredAlert } from "../../../session";
import PatientAgreementsSummaryCard from "../components/PatientAgreementsSummaryCard";
import Chart from "react-apexcharts";
import type { ApexOptions } from "apexcharts";
import PatientToolDock from "../components/PatientToolDock";
import PatientNextSession from "../components/PatientNextSession";

type TabId = 'overview' | 'sessions' | 'therapeuticPlans' | 'recommendations' | 'alerts' | 'aiChat' | 'patientAgreements' | 'highlights';

// AI Insights tool surfaces — the whole group disappears without adoption.
const AI_TAB_IDS: TabId[] = ['recommendations', 'alerts', 'aiChat'];
// Core follow-up work — always available.
const FOLLOW_UP_TAB_IDS: TabId[] = ['patientAgreements', 'highlights'];
const GROUPED_TAB_IDS: TabId[] = [...AI_TAB_IDS, ...FOLLOW_UP_TAB_IDS];

interface AlertCounts { critical: number; medium: number; low: number; }

function CoupleAlertTypesPieCard({ coupleId, onViewAll, t }: { coupleId: string; onViewAll: () => void; t: ReturnType<typeof useTranslation>["t"] }) {
    const [loading, setLoading] = useState(true);
    const [counts, setCounts] = useState<AlertCounts>({ critical: 0, medium: 0, low: 0 });
    const [total, setTotal] = useState(0);

    useEffect(() => {
        listCoupleSessions(coupleId, { limit: 100 })
            .then((data) => {
                const sessions = data.sessions ?? [];
                const c: AlertCounts = { critical: 0, medium: 0, low: 0 };
                sessions.forEach((s) => {
                    (s.triggered_alerts as TriggeredAlert[] ?? []).forEach((a) => {
                        const lower = (a.criticality ?? "").toLowerCase();
                        if (lower.includes("crítica") || lower.includes("critical")) c.critical++;
                        else if (lower.includes("media") || lower.includes("medium")) c.medium++;
                        else c.low++;
                    });
                });
                setCounts(c);
                setTotal(c.critical + c.medium + c.low);
            })
            .finally(() => setLoading(false));
    }, [coupleId]);

    const rawSeries = [counts.critical, counts.medium, counts.low];
    const rawLabels = [t("patient.alerts.critical", "Críticas"), t("patient.alerts.medium", "Medias"), t("patient.alerts.low", "Bajas")];
    const rawColors = ["#f87171", "#fbbf24", "#7592ff"];
    const filtered = rawSeries.map((v, i) => ({ v, l: rawLabels[i], c: rawColors[i] })).filter(x => x.v > 0);
    const series = filtered.map(x => x.v);
    const labels = filtered.map(x => x.l);
    const colors = filtered.map(x => x.c);

    const options: ApexOptions = {
        chart: { type: "donut", fontFamily: "inherit", animations: { enabled: true, speed: 600 }, toolbar: { show: false } },
        labels, colors,
        stroke: { show: false },
        dataLabels: { enabled: false },
        legend: { show: false },
        plotOptions: {
            pie: {
                expandOnClick: false,
                donut: {
                    size: "78%",
                    labels: {
                        show: true,
                        total: { show: true, label: t("patient.alerts.total", "Alertas"), color: "#94a3b8", fontSize: "11px", formatter: () => String(total) },
                        value: { show: false },
                        name: { show: true, fontSize: "10px", color: "#94a3b8", offsetY: 18 },
                    },
                },
            },
        },
        tooltip: { enabled: true, theme: "light", style: { fontSize: "11px" } },
    };

    return (
        <div className="glass-effect glass-border border rounded-2xl p-5 shadow-theme-sm flex flex-col">
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center shrink-0">
                        <Bell className="w-4 h-4 text-red-500 dark:text-red-400" />
                    </div>
                    <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">{t("patient.alertTypes", "Tipos de alerta")}</h3>
                </div>
                <button onClick={onViewAll} className="flex items-center gap-0.5 text-xs font-medium text-brand-600 dark:text-brand-400 hover:gap-1 transition-all">
                    {t("common.viewAll")} <ChevronRight className="w-3.5 h-3.5" />
                </button>
            </div>
            {loading ? (
                <div className="flex-1 flex items-center justify-center py-4">
                    <div className="h-24 w-24 bg-gray-100 dark:bg-white/5 rounded-full animate-pulse" />
                </div>
            ) : total === 0 ? (
                <p className="text-sm text-content-tertiary italic py-2">{t("patient.alerts.none", "Sin alertas registradas")}</p>
            ) : (
                <div className="flex flex-col items-center gap-3">
                    <div className="w-full max-w-[160px]">
                        <Chart options={options} series={series} type="donut" width="100%" />
                    </div>
                    <div className="flex flex-wrap gap-2 justify-center">
                        {counts.critical > 0 && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-400"><span className="w-2 h-2 rounded-full bg-red-400 shrink-0" />{counts.critical} {t("patient.alerts.critical", "Críticas")}</span>}
                        {counts.medium > 0 && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-yellow-50 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400"><span className="w-2 h-2 rounded-full bg-yellow-400 shrink-0" />{counts.medium} {t("patient.alerts.medium", "Medias")}</span>}
                        {counts.low > 0 && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-400"><span className="w-2 h-2 rounded-full bg-brand-400 shrink-0" />{counts.low} {t("patient.alerts.low", "Bajas")}</span>}
                    </div>
                </div>
            )}
        </div>
    );
}

interface CoupleOverviewProps {
    couple: CouplePatient;
    isPremium: boolean;
    onTabChange: (tab: TabId) => void;
    onDelete: () => void;
    deleting: boolean;
    navigate: (path: string) => void;
    t: ReturnType<typeof useTranslation>["t"];
    aiInsightsAdopted: boolean;
}

function CoupleOverview({ couple, isPremium, onTabChange, onDelete, deleting, navigate, t, aiInsightsAdopted }: CoupleOverviewProps) {
    // Whose couple this is — only meaningful inside a centre.
    const { center } = useCenter();
    return (
        <div className="space-y-8">
            {/* Row 1: Next scheduled session, when the Scheduler is adopted.
                Kept out of the grid below because that row is gated on the
                premium plan and this is gated on tool adoption. */}
            {couple.id && (
                <PatientNextSession
                    kind="couple"
                    id={couple.id}
                    label={couple.display_name}
                />
            )}

            {/* Row 2: Alert types pie (AI Insights) | Agreements status (core) */}
            {isPremium && couple.id && (
                <div className={`grid grid-cols-1 gap-4 ${aiInsightsAdopted ? "sm:grid-cols-2" : ""}`}>
                    {aiInsightsAdopted && <CoupleAlertTypesPieCard coupleId={couple.id} onViewAll={() => onTabChange("alerts")} t={t} />}
                    <PatientAgreementsSummaryCard patientId={couple.id} isCouple={true} onViewAll={() => onTabChange("patientAgreements")} />
                </div>
            )}

            {/* Row 2: 3 columns — Couple info | Patient 1 | Patient 2 */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {/* Couple info */}
                <ComponentCard glass={true} title="">
                    <div className="flex flex-col items-center gap-4 p-2">
                        <div className={`w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold uppercase ${getAvatarColor(couple.id ?? "")}`}>
                            <Users className="w-8 h-8" />
                        </div>
                        <div className="text-center">
                            <h2 className="text-base font-bold text-content-primary">{couple.display_name}</h2>
                            {center && (
                                <PatientOwnershipBadge
                                    heldByCenter={Boolean(couple.patient_1?.center_id)}
                                    className="mt-2"
                                />
                            )}
                            <div className="flex items-center justify-center gap-1 text-xs text-content-tertiary mt-1">
                                <Calendar className="w-3.5 h-3.5" />
                                <span>{t("patient.since")}: {couple.created_at ? new Date(couple.created_at).toLocaleDateString() : "-"}</span>
                            </div>
                        </div>
                        <div className="w-full mt-4 pt-4 border-t border-red-100 dark:border-red-900/20">
                            <Button
                                variant="outline"
                                className="w-full text-red-500 hover:text-red-600 hover:bg-red-50 border-red-200 hover:border-red-300 dark:border-red-900/40 dark:hover:bg-red-950/30 transition-all"
                                onClick={onDelete}
                                loading={deleting}
                                startIcon={<Trash2 className="w-3.5 h-3.5" />}
                                size="sm"
                            >
                                {deleting
                                    ? t("common.deleting")
                                    : t("patient.deletePatient", "Eliminar Paciente")}
                            </Button>
                        </div>
                    </div>
                </ComponentCard>

                {/* Patient 1 */}
                <ComponentCard title={t("patient.patient1")} glass={true}>
                    {couple.patient_1 ? (
                        <div className="space-y-3">
                            <div className="flex items-center gap-3">
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-white shrink-0 ${getAvatarColor(couple.patient_1.id)}`}>
                                    {couple.patient_1.first_name[0]}
                                </div>
                                <div className="min-w-0">
                                    <p className="font-semibold text-content-primary text-sm truncate">
                                        {couple.patient_1.first_name} {couple.patient_1.last_name}
                                    </p>
                                    <button onClick={() => navigate(`/patient/${couple.patient_1?.id}`)} className="text-xs text-brand-500 hover:underline">
                                        {t("patient.viewProfile")}
                                    </button>
                                </div>
                            </div>
                            <div className="space-y-1.5 text-xs text-content-secondary">
                                <div className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 shrink-0" /><span className="truncate">{couple.patient_1.contact_email}</span></div>
                                <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 shrink-0" /><span>{couple.patient_1.contact_phone}</span></div>
                            </div>
                        </div>
                    ) : (
                        <p className="text-sm text-gray-500">{t("patient.notFound")}</p>
                    )}
                </ComponentCard>

                {/* Patient 2 */}
                <ComponentCard title={t("patient.patient2")} glass={true}>
                    {couple.patient_2 ? (
                        <div className="space-y-3">
                            <div className="flex items-center gap-3">
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-white shrink-0 ${getAvatarColor(couple.patient_2.id)}`}>
                                    {couple.patient_2.first_name[0]}
                                </div>
                                <div className="min-w-0">
                                    <p className="font-semibold text-content-primary text-sm truncate">
                                        {couple.patient_2.first_name} {couple.patient_2.last_name}
                                    </p>
                                    <button onClick={() => navigate(`/patient/${couple.patient_2?.id}`)} className="text-xs text-brand-500 hover:underline">
                                        {t("patient.viewProfile")}
                                    </button>
                                </div>
                            </div>
                            <div className="space-y-1.5 text-xs text-content-secondary">
                                <div className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 shrink-0" /><span className="truncate">{couple.patient_2.contact_email}</span></div>
                                <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 shrink-0" /><span>{couple.patient_2.contact_phone}</span></div>
                            </div>
                        </div>
                    ) : (
                        <p className="text-sm text-gray-500">{t("patient.notFound")}</p>
                    )}
                </ComponentCard>
            </div>

            {/* Row 3: Last session summary (AI Insights) */}
            {isPremium && aiInsightsAdopted && couple.id && (
                <div className="glass-effect glass-border border rounded-3xl p-8 shadow-theme-md bg-gradient-to-br from-white/80 to-brand-50/30 dark:from-white/[0.02] dark:to-brand-500/5">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-10 h-10 rounded-xl bg-purple-500/10 text-purple-600 flex items-center justify-center">
                            <Sparkles className="w-6 h-6" />
                        </div>
                        <h3 className="text-xl font-bold text-content-primary">{t("patient.lastSessionSummary")}</h3>
                    </div>
                    <div className="min-h-[100px]">
                        <RecommendationsCard patientId={couple.id} isCouple={true} hideHeader={true} summaryOnly={true} />
                    </div>
                    <div className="mt-4 pt-4 border-t border-brand-100 dark:border-white/5 flex justify-end">
                        <button onClick={() => onTabChange("recommendations")} className="text-brand-600 font-bold text-sm flex items-center gap-1 hover:gap-1.5 transition-all">
                            {t("patient.viewRecommendations")} <ChevronRight className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            )}

            {/* Row 4: Session history */}
            <div>
                <div className="flex justify-between items-center mb-6">
                    <h3 className="text-xl font-bold text-content-primary flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-gray-100 dark:bg-white/5 text-gray-500 flex items-center justify-center">
                            <Calendar className="w-5 h-5" />
                        </div>
                        {t("patient.recentSessions")}
                    </h3>
                    <button onClick={() => onTabChange("sessions")} className="text-brand-600 font-semibold text-sm hover:underline">
                        {t("common.showMore")}
                    </button>
                </div>
                <PatientViewSessions patientID={couple.id!} isCouple={true} pageSize={5} hideHeader={true} simple={true} />
            </div>
        </div>
    );
}

export default function CouplePatientPage() {
    const { t } = useTranslation();
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { couple, loading, error, deleteCouple } = useCouplePatient(id);
    const { isPremium } = useSubscription();
    const { adopted: aiInsightsAdopted, loading: adoptionLoading } = useAiInsightsGate();

    const [searchParams, setSearchParams] = useSearchParams();
    const initialTab = (searchParams.get("tab") as TabId) || 'overview';
    const [activeTab, setActiveTabInternal] = useState<TabId>(initialTab);
    const { reduced } = useMotionSafe();
    const [deleting, setDeleting] = useState(false);

    const setActiveTab = (tab: TabId) => {
        setActiveTabInternal(tab);
        setSearchParams({ tab });
    };

    useEffect(() => {
        const tab = searchParams.get("tab");
        if (tab && tab !== activeTab) {
            setActiveTabInternal(tab as TabId);
        }
    }, [searchParams, activeTab]);

    // The AI Insights group disappears without tool adoption; fall back to overview.
    useEffect(() => {
        if (adoptionLoading || aiInsightsAdopted) return;
        if (AI_TAB_IDS.includes(activeTab)) setActiveTab('overview');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [adoptionLoading, aiInsightsAdopted, activeTab]);

    const handleDelete = async () => {
        if (!confirm(t("common.confirmDelete"))) return;
        setDeleting(true);
        const success = await deleteCouple();
        setDeleting(false);
        if (success) {
            navigate("/patients");
        }
    };

    const tabs = [
        { id: 'overview', label: t("patient.tabs.overview"), icon: <LayoutDashboard className="w-4 h-4" /> },
        { id: 'sessions', label: t("patient.tabs.sessions"), icon: <HistoryIcon className="w-4 h-4" /> },
        { id: 'therapeuticPlans', label: t("patient.tabs.therapeuticPlans"), icon: <FileText className="w-4 h-4" /> },
        { id: 'recommendations', label: t("patient.tabs.recommendations"), icon: <Lightbulb className="w-4 h-4" /> },
        { id: 'alerts', label: t("patient.tabs.alerts"), icon: <AlertTriangle className="w-4 h-4" /> },
        { id: 'aiChat', label: t("patient.tabs.aiChat"), icon: <MessageSquare className="w-4 h-4" /> },
        { id: 'patientAgreements', label: t("patient.tabs.patientAgreements"), icon: <ListChecks className="w-4 h-4" /> },
        { id: 'highlights', label: t("patient.tabs.highlights", "Highlights"), icon: <Highlighter className="w-4 h-4" /> },
    ].filter(tab => {
        if (!isPremium && (tab.id === 'therapeuticPlans' || tab.id === 'recommendations' || tab.id === 'alerts' || tab.id === 'aiChat' || tab.id === 'patientAgreements' || tab.id === 'highlights')) {
            return false;
        }
        // The AI Insights group (and its header) disappears without tool adoption.
        if (!adoptionLoading && !aiInsightsAdopted && AI_TAB_IDS.includes(tab.id as TabId)) {
            return false;
        }
        return true;
    });

    const mainTabs = tabs.filter(t => !GROUPED_TAB_IDS.includes(t.id as TabId));

    const menuGroups = [
        {
            id: 'aiInsights',
            label: t("patient.tabs.aiInsights"),
            icon: <Sparkles className="w-4 h-4" />,
            tabs: tabs.filter(tab => AI_TAB_IDS.includes(tab.id as TabId)),
        },
        {
            id: 'followUp',
            label: t("patient.tabs.followUp"),
            icon: <BarChart2 className="w-4 h-4" />,
            tabs: tabs.filter(tab => FOLLOW_UP_TAB_IDS.includes(tab.id as TabId)),
        },
    ].filter(group => group.tabs.length > 0);

    const PRE_GROUP_IDS: TabId[] = ['overview', 'sessions'];
    const preGroupTabs = mainTabs.filter(t => PRE_GROUP_IDS.includes(t.id as TabId));
    const postGroupTabs = mainTabs.filter(t => !PRE_GROUP_IDS.includes(t.id as TabId));

    const activeTabData = tabs.find(t => t.id === activeTab) || tabs[0];

    if (loading) {
        return (
            <div className="flex justify-center items-center min-h-[400px]">
                <Loader2 className="animate-spin w-10 h-10 text-brand-500 opacity-50" />
            </div>
        );
    }

    if (error || !couple) {
        return (
            <div className="p-8 text-center text-red-500">
                {error || t("patient.notFound")}
            </div>
        );
    }

    return (
        <div className="min-h-screen pb-20 md:pr-24">
            <PageMeta title={couple.display_name} />
            {couple.id && (
                <PatientToolDock
                    couplePatientId={couple.id}
                    subjectLabel={couple.display_name}
                />
            )}

            <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
                <PageHeader
                    className="mb-6"
                    eyebrow={t("patient.coupleTab")}
                    title={couple.display_name}
                />
                {/* Responsive Navigation */}
                <div className="w-full mb-10 relative z-30">
                    <GroupedTabNav
                        label={couple.display_name}
                        panelId={PANEL_ID}
                        activeTab={activeTab}
                        onChange={(id) => setActiveTab(id)}
                        leadingTabs={preGroupTabs}
                        groups={menuGroups}
                        trailingTabs={postGroupTabs}
                        layoutId="active-tab-pill-couple"
                    />
                </div>

                <AnimatePresence mode="wait">
                    <motion.div
                        key={activeTab}
                        id={PANEL_ID}
                        role="tabpanel"
                        aria-label={activeTabData.label}
                        initial={{ opacity: 0, y: reduced ? 0 : 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: reduced ? 0 : -6 }}
                        transition={{ duration: reduced ? 0.12 : 0.2 }}
                    >
                        {/* Content Area */}
                        <div className="space-y-8">
                            {/* Overview Tab */}
                            {activeTab === 'overview' && (
                                <CoupleOverview
                                    couple={couple}
                                    isPremium={isPremium}
                                    onTabChange={setActiveTab}
                                    onDelete={handleDelete}
                                    deleting={deleting}
                                    navigate={navigate}
                                    t={t}
                                    aiInsightsAdopted={aiInsightsAdopted}
                                />
                            )}

                            {/* Recommendations Tab */}
                            {activeTab === 'recommendations' && (
                                <div className="w-full">
                                    <RecommendationsCard patientId={couple.id!} isCouple={true} />
                                </div>
                            )}

                            {/* Alerts Tab */}
                            {activeTab === 'alerts' && isPremium && (
                                <div className="w-full">
                                    <PatientAlertsTab patientID={couple.id!} isCouple={true} />
                                </div>
                            )}

                            {/* Therapeutic Plans */}
                            {activeTab === 'therapeuticPlans' && (
                                <div className="w-full">
                                    {!isPremium ? (
                                        <SubscriptionRestrictedState
                                            title={t("therapeuticPlan.restricted.title")}
                                            description={t("therapeuticPlan.restricted.description")}
                                        />
                                    ) : (
                                        id && <PatientViewTherapeuticPlans patientID={id} isCouple={true} />
                                    )}
                                </div>
                            )}

                            {/* Sessions */}
                            {activeTab === 'sessions' && (
                                <div className="w-full">
                                    {id && <PatientViewSessions patientID={id} isCouple={true} />}
                                </div>
                            )}

                            {/* AI Chat */}
                            {activeTab === 'aiChat' && isPremium && (
                                <div className="w-full">
                                    <CouplePatientChatTab couplePatient={couple} persist={true} />
                                </div>
                            )}

                            {/* Agreements */}
                            {activeTab === 'patientAgreements' && isPremium && (
                                <div className="w-full">
                                    <PatientAgreementsTab patientId={couple.id!} isCouple={true} />
                                </div>
                            )}

                            {/* Highlights */}
                            {activeTab === 'highlights' && isPremium && (
                                <div className="w-full">
                                    <PatientHighlightsTab patientId={couple.id!} isCouple={true} />
                                </div>
                            )}
                        </div>
                    </motion.div>
                </AnimatePresence>
            </div>
        </div>
    );
}
